import { sound } from '@pixi/sound';
import { AnimatedSprite, Container, Graphics, Sprite, Text, Texture } from 'pixi.js';
import { Scene, Store, TextureFactory } from '../core';
import DrawingGameHealth from './DrawingGameHealth';
import GameOverUI from './GameOverUI';
import ScoreText from './ScoreText';
import VanishTitle from './VanishTitle';
import TextureInfo from '../class/final/fx2.json';
import NotificationNinimo from './NotificationNinimo';
import { GlowFilter } from 'pixi-filters';
import { applyRanks } from '../api/rank';
import { LOCALE_DATA } from '../constant';

class GetItemPopup extends Container {
  private itemSprite: Sprite;
  private itemTextures: Array<Texture>;
  private title: Text;
  private note: Text;

  constructor() {
    super();

    const bgSprite = new Sprite(TextureFactory.instance.getSingleTexture('/textures/ui/background_effect.png'));
    bgSprite.anchor.set(0.5);
    this.addChild(bgSprite);

    const baseSprite = new Sprite(TextureFactory.instance.getSingleTexture('/textures/ui/popup_item_get.png'));
    baseSprite.anchor.set(0.5);
    this.addChild(baseSprite);

    this.itemTextures = [
      TextureFactory.instance.getSingleTexture('/textures/ui/item_1.png'),
      TextureFactory.instance.getSingleTexture('/textures/ui/item_2.png'),
      TextureFactory.instance.getSingleTexture('/textures/ui/item_3.png'),
      TextureFactory.instance.getSingleTexture('/textures/ui/item_4.png'),
      TextureFactory.instance.getSingleTexture('/textures/ui/item_5.png'),
      TextureFactory.instance.getSingleTexture('/textures/ui/item_6.png'),
    ];

    this.itemSprite = new Sprite(this.itemTextures[0]);
    this.itemSprite.scale.set(2);
    this.itemSprite.anchor.set(0.5);
    this.itemSprite.x = -160;
    this.itemSprite.y = -40;
    this.addChild(this.itemSprite);

    this.title = new Text(LOCALE_DATA[Store.instance.state.locale].GET_ITEM, {
      fontSize: Store.instance.state.locale === 'en-US' ? 50 : 80,
      fontFamily: 'gaegu',
      fontWeight: '400',
      fontStyle: 'normal',
      fill: '0xffffff',
      stroke: '0x492A2E',
      strokeThickness: 16,
      lineJoin: 'round',
      padding: 20,
    });
    this.title.anchor.set(0.5);
    this.title.y = -525;
    this.addChild(this.title);

    this.note = new Text(LOCALE_DATA[Store.instance.state.locale].GET_ITEM_DESCRIPTION_1, {
      fontSize: 40,
      fontFamily: 'gaegu',
      fontWeight: '400',
      fontStyle: 'normal',
      fill: '0xffffff',
      lineJoin: 'round',
      padding: 10,
    });
    this.note.anchor.set(0.5);
    this.note.x = 55;
    this.note.y = 440;
    this.addChild(this.note);
  }

  public setItem(item: number): void {
    this.itemSprite.texture = this.itemTextures[item - 1];
    const notes = [
      LOCALE_DATA[Store.instance.state.locale].GET_ITEM_DESCRIPTION_1,
      LOCALE_DATA[Store.instance.state.locale].GET_ITEM_DESCRIPTION_2,
      LOCALE_DATA[Store.instance.state.locale].GET_ITEM_DESCRIPTION_3,
    ];
    this.note.text = notes[Math.floor(notes.length * Math.random())];
  }
}

class DrawingGameUI extends Container {
  private readonly scene: Scene;
  private readonly health: DrawingGameHealth;
  private readonly score: ScoreText;
  private readonly vanishTitle: VanishTitle;
  private readonly gameover: GameOverUI;
  private readonly notificationText: Text;
  private lightingFx: Graphics;
  private focusFx: AnimatedSprite;
  public gameState: { stage: string; score: string; rank: number } = { stage: '', score: '', rank: -1 };
  public showNotificationText: boolean;
  public notificationNinimo: NotificationNinimo;
  public notificationNinimoDT: number;
  public getItemPopup: GetItemPopup;
  public showItemDT: number;
  public pausedButton: AnimatedSprite;
  public paused: boolean = false;

  constructor(scene: Scene) {
    super();
    this.scene = scene;
    this.showNotificationText = false;
    this.showItemDT = 0;

    const textures = TextureFactory.instance.load(TextureInfo);
    this.focusFx = new AnimatedSprite(textures);
    this.focusFx.width = this.scene.game.width;
    this.focusFx.height = this.scene.game.height;
    this.focusFx.loop = true;
    this.focusFx.animationSpeed = 0.2;
    this.focusFx.updateAnchor = true;
    this.focusFx.play();
    this.focusFx.position.set(this.scene.game.width / 2, this.scene.game.height / 2);
    this.focusFx.visible = false;
    this.addChild(this.focusFx);

    this.lightingFx = new Graphics();
    this.lightingFx.beginFill(0xffffff, 0.5);
    this.lightingFx.drawRect(0, 0, 1080, 1920);
    this.lightingFx.endFill();
    this.lightingFx.visible = false;
    this.addChild(this.lightingFx);

    this.health = new DrawingGameHealth();
    this.health.x = 10;
    this.health.y = 10;
    this.health.scale.set(0.8);
    this.addChild(this.health);

    this.score = new ScoreText();
    this.score.x = 10;
    this.score.y = 10;
    this.addChild(this.score);

    this.pausedButton = new AnimatedSprite([
      TextureFactory.instance.getSingleTexture('/textures/ui/pause.png'),
      TextureFactory.instance.getSingleTexture('/textures/ui/start.png'),
    ]);
    this.pausedButton.filters = [new GlowFilter({ outerStrength: 1, color: 0xffffff, distance: 5, quality: 1 })];
    this.pausedButton.anchor.set(0.5);
    this.pausedButton.scale.set(0.5);
    this.pausedButton.buttonMode = true;
    this.pausedButton.interactive = true;
    this.pausedButton.on('pointerdown', () => {
      if (this.paused) {
        this.pausedButton.gotoAndStop(0);
        this.emit('start');
        this.paused = false;
      } else {
        this.pausedButton.gotoAndStop(1);
        this.emit('pause');
        this.paused = true;
      }
    });
    this.addChild(this.pausedButton);

    this.notificationText = new Text('', {
      fontSize: 80,
      fontWeight: '700',
      fontFamily: 'gaegu',
      fontStyle: 'normal',
      stroke: 0xffffff,
      fill: '0x4a282f',
      strokeThickness: 6,
      padding: 22,
      align: 'center',
    });
    this.notificationText.visible = false;
    this.notificationText.anchor.set(0.5);
    this.addChild(this.notificationText);

    this.vanishTitle = new VanishTitle();
    this.addChild(this.vanishTitle);

    this.gameover = new GameOverUI();
    this.gameover.visible = false;
    this.addChild(this.gameover);

    this.gameover.on('home', () => {
      this.emit('home');
    });

    this.gameover.on('ad', () => {
      this.emit('ad');
    });

    this.gameover.on('share', () => {
      this.notificationNinimoDT = 100;
    });

    this.notificationNinimo = new NotificationNinimo();
    this.notificationNinimo.x = this.scene.game.width;
    this.notificationNinimo.y = this.scene.game.height + this.notificationNinimo.height;
    this.addChild(this.notificationNinimo);

    this.getItemPopup = new GetItemPopup();
    this.getItemPopup.visible = false;
    this.addChild(this.getItemPopup);

    this.notificationNinimoDT = 0;
  }

  public update(elapsed: number): void {
    this.health.update(elapsed);
    this.gameover.update(elapsed);
    this.score.update(elapsed);
    this.score.x = this.scene.game.width - this.score.width - 48;
    this.score.y = 4;

    this.pausedButton.x = this.scene.game.width - 23;
    this.pausedButton.y = 26;

    this.vanishTitle.scale.set(this.scene.camera.zoom);
    this.vanishTitle.y = this.scene.game.height / 2;
    this.vanishTitle.x = this.scene.game.width / 2;
    this.vanishTitle.update(elapsed);

    this.gameover.scale.set(this.scene.camera.zoom);
    this.gameover.x = this.scene.game.width / 2;
    this.gameover.y = this.scene.game.height / 2;

    this.notificationText.scale.set(this.scene.camera.zoom);
    this.notificationText.x = this.scene.game.width / 2;
    this.notificationText.y = this.scene.game.height / 2 - 200;

    this.getItemPopup.scale.set(this.scene.camera.zoom);
    this.getItemPopup.x = this.scene.game.width / 2;
    this.getItemPopup.y = this.scene.game.height / 2;

    this.notificationNinimo.x = this.scene.game.width - 10;
    if (this.notificationNinimoDT > 0 && Math.abs(this.scene.game.height - this.notificationNinimo.y) >= 1) {
      this.notificationNinimo.y += (this.scene.game.height - this.notificationNinimo.y) * 0.1 * elapsed;
    } else if (this.notificationNinimoDT > 0) {
      this.notificationNinimo.y = this.scene.game.height;
      this.notificationNinimoDT -= elapsed;
    } else if (
      this.notificationNinimoDT <= 0 &&
      Math.abs(this.scene.game.height + this.notificationNinimo.height - this.notificationNinimo.y) >= 1
    ) {
      this.notificationNinimo.y +=
        (this.scene.game.height + this.notificationNinimo.height - this.notificationNinimo.y) * 0.1 * elapsed;
    } else {
      this.notificationNinimo.y = this.scene.game.height + this.notificationNinimo.height;
      this.notificationNinimoDT = 0;
    }

    if (!this.showNotificationText && this.notificationText.alpha >= 0.04 * elapsed) {
      this.notificationText.alpha -= 0.04 * elapsed;
    } else if (!this.showNotificationText) {
      this.notificationText.alpha = 0;
      this.notificationText.visible = false;
    }

    if (this.showNotificationText && this.notificationText.alpha <= 1 - 0.04 * elapsed) {
      this.notificationText.visible = true;
      this.notificationText.alpha += 0.04 * elapsed;
    } else if (this.showNotificationText) {
      this.notificationText.alpha = 1;
    }

    if (this.lightingFx.alpha >= 0.02 * elapsed) {
      this.lightingFx.alpha -= 0.02 * elapsed;
    } else {
      this.lightingFx.alpha = 0;
      this.lightingFx.visible = false;
    }

    if (this.showItemDT > 0 && this.getItemPopup.alpha <= 0.97) {
      this.getItemPopup.alpha += 0.03;
      this.getItemPopup.visible = true;
    } else if (this.showItemDT > 0) {
      this.getItemPopup.alpha = 1;
      this.showItemDT -= elapsed;
    } else if (this.showItemDT <= 0 && this.getItemPopup.alpha >= 0.03) {
      this.getItemPopup.alpha -= 0.03;
    } else if (this.getItemPopup.visible) {
      this.getItemPopup.alpha = 0;
      this.getItemPopup.visible = false;
      this.emit('POPUP_END');
    }
  }

  public showGetItemPopup(item: number): void {
    this.getItemPopup.alpha = 0;
    this.getItemPopup.visible = false;
    this.getItemPopup.setItem(item);
    this.showItemDT = 200;
    sound.play('/audio/getting_item.mp3', { loop: false, volume: Store.instance.state.volume as number });
  }

  public focus(): void {
    this.focusFx.visible = true;
  }

  public unFocus(): void {
    this.focusFx.visible = false;
  }

  public lighting(): void {
    this.lightingFx.visible = true;
    this.lightingFx.alpha = 1;
  }

  public showNotification(text: string): void {
    this.notificationText.alpha = 0;
    this.notificationText.visible = true;
    this.notificationText.text = text;
    this.showNotificationText = true;
  }

  public hideNotification(): void {
    this.showNotificationText = false;
  }

  public showTitle(title: string): void {
    this.vanishTitle.setTitle(title);
    this.vanishTitle.initialize();
  }

  public setHP(hp: number): void {
    this.health.setHP(hp);
  }

  public setScore(score: number): void {
    this.score.setTargetScore(score);
  }

  public async showGameOver(stage: string, score: string): Promise<void> {
    let rank = -1;
    try {
      rank = await applyRanks(
        Store.instance.e(`${stage},${score},${Store.instance.data.playername}`, '89Gr9JE3HLD0KEMeAsbKCC7XLts5552w')
      );
    } catch (e) {
      console.error(e);
    }
    sound.play('/audio/gameover_popup.mp3', { loop: false, volume: Store.instance.state.volume as number });
    this.gameState.stage = stage;
    this.gameState.score = score;
    this.gameState.rank = rank;
    this.gameover.setScore(stage, score, rank, Store.instance.data.playername);
    this.gameover.visible = true;

    const button = document.getElementById('sharebtn') as HTMLElement;
    button.style.display = 'block';
  }
}

export default DrawingGameUI;
